/**
 * GLB export (SPEC "GLB Export"): the project's root, its skeleton, and — optionally — the library
 * clips ticked for embedding, baked through the rig binding into rotation-only tracks.
 *
 * Zero clips is the DEFAULT shape, not an error: a character ships as a light rig-only GLB and its
 * animations travel as shared JSON clips played by the runtime SDK. Embedding is the escape hatch
 * for an SDK-less consumer, so the export refuses nothing when nothing is ticked.
 *
 * "Export success is not established merely because a Blob was created": `exportGlb` already
 * re-loads and validates the buffer, and the inspection is re-run here so the caller (and the
 * acceptance tests) can assert on what a consumer would actually see.
 */

import { AppError, RIG_VERSION, textureManifestToJson } from '../../shared';
import type { CanonicalAnimationClip } from '../../shared';
import { downloadBlob, exportGlb, extractTextures, inspectGlb } from '../../importExport';
import type { ExtractedTextures, GlbInspection } from '../../importExport';
import { clipsRepo } from '../../persistence';
import { safeFileName } from './libraryController';
import type { ControllerDeps } from './internal';
import { guard, requireProject } from './internal';
import type { ExportController, ExportGlbOptions } from './types';

function withGlbExtension(name: string): string {
  const trimmed = name.trim() || 'model';
  return /\.glb$/i.test(trimmed) ? trimmed : `${trimmed}.glb`;
}

/** `"knight.glb"` → `"knight"`, the stem the manifest and every image file are named from. */
function stemOf(filename: string): string {
  return filename.replace(/\.glb$/i, '') || 'model';
}

/**
 * What to say about the texture side of a stripped export.
 *
 * Phrased as guidance, not as a result, for the same reason `downloadClipFiles` is: Chrome asks
 * before the second download of a batch and whether the user allowed it is NOT observable from JS,
 * so "3 files downloaded" would be a guess. "No textures to extract" is a real outcome and is said
 * plainly rather than dressed up as a success.
 */
function textureNote(extracted: ExtractedTextures | null): string {
  if (!extracted) return '';
  if (extracted.files.length === 0) return ' This model has no textures to extract.';
  const kb = Math.round(extracted.totalBytes / 1024);
  return (
    ` Textures left out: ${extracted.files.length} image file(s) + manifest, ${kb} KB — ` +
    'save them beside the GLB and allow multiple downloads if the browser asks.'
  );
}

export function createExportController(deps: ControllerDeps): ExportController {
  const { ctx } = deps;
  const { store, session } = ctx;

  /**
   * Name of the open clip when the last `exportGlbBuffer` shipped it with unsaved edits, so the
   * download path can say so in its summary.
   *
   * A closure flag rather than an extra field on the returned object: `ExportController` is a
   * pinned cross-coder contract (`controllers/types.ts`) and this is a message detail, not part
   * of what an export IS. Read immediately after the awaited call, never across one.
   */
  let unsavedClipName: string | null = null;

  const controller: ExportController = {
    async exportGlbBuffer(
      options: ExportGlbOptions = {},
    ): Promise<{ buffer: ArrayBuffer; inspection: GlbInspection }> {
      return guard(ctx, 'The GLB could not be exported.', async () => {
        const project = requireProject(ctx);
        const rig = session.rig;
        if (!rig) {
          throw new AppError(
            'EXPORT_NO_RIG',
            project.kind === 'rigged'
              ? 'Confirm the bone mapping before exporting.'
              : 'Generate the skin weights before exporting.',
          );
        }

        // A mapped model exports its own scene graph; a tool-rigged one exports the Group that
        // holds the SkinnedMesh and the Root bone at identity.
        const root =
          project.kind === 'rigged' ? session.model?.root : deps.controllers.rig.riggedRoot();
        if (!root) {
          throw new AppError('EXPORT_NO_MODEL', 'There is no model to export.');
        }

        const selectedIds = store.getState().library.selectedClipIds;
        const fetched = await Promise.all(selectedIds.map((id) => clipsRepo.get(id)));

        /*
         * The clip on SCREEN beats the clip on disk.
         *
         * Clips are not auto-saved, so `state.clip` and `clipsRepo` drift apart the moment the
         * user sets a key. Before this, the export read the repository only: the audit reproduced
         * it live (§2.2 #1) — three keyframes on screen, `clipDirty === true`, one keyframe in the
         * store, and the "successful" GLB carried a single track. The toast said "Exported 1
         * clip(s), 22 bones." with no warning, no dialog and no console line. That is the same
         * silent-data-loss class the `missing` guard right below already refuses to allow.
         *
         * The open clip is SUBSTITUTED IN MEMORY, never saved. Two reasons this path must not
         * write: `exportGlbBuffer` is the round-trip probe (see `exportGlbDownload` below) and a
         * probe must not alter what the user last shipped; and `newClip()` marks a clip dirty
         * immediately, so saving here would push half-authored clips into the library behind the
         * user's back.
         *
         * Only a DIRTY open clip substitutes. A clean one is byte-identical to its record, so
         * reading the record keeps the "a selected clip vanished from the library" guard honest:
         * a clean clip that is missing from the repository really was deleted underneath us.
         */
        const openClip = store.getState().clip;
        const openIsDirty = store.getState().clipDirty;
        const liveId = openClip && openIsDirty ? openClip.id : null;
        unsavedClipName = null;

        const clips: CanonicalAnimationClip[] = [];
        const missing: string[] = [];
        selectedIds.forEach((id, i) => {
          if (openClip && id === liveId) {
            clips.push(openClip);
            unsavedClipName = openClip.name;
            return;
          }
          const clip = fetched[i];
          if (clip) clips.push(clip);
          else missing.push(id);
        });
        /*
         * A partial export is a silent data loss: the user selected five animations, one is gone
         * from the library, and the "successful" GLB ships with four — discovered weeks later in
         * the game (audit P1). Whatever happened to that clip, the fix is never to quietly export
         * around it, so the export stops and names the ids that could not be found.
         */
        if (missing.length > 0) {
          const known = store.getState().library.clips;
          const names = missing.map((id) => known.find((c) => c.id === id)?.name ?? id);
          throw new AppError(
            'EXPORT_CLIP_MISSING',
            `${missing.length} selected animation(s) are no longer in the library ` +
              `(${names.join(', ')}). Restore them, or clear them from this model's selection, ` +
              'before exporting.',
            { missing, names },
          );
        }
        // Both live slices: `beforeExport` resets the rig to rest, so `afterExport` has to put
        // the hips back too — a one-argument `applyPose` would leave the pelvis at bind height
        // and the user would watch their crouch stand up when the download finished.
        const { livePose, liveHipsY } = store.getState();
        const buffer = await exportGlb({
          root,
          clips,
          rig,
          metadata: {
            rigVersion: RIG_VERSION,
            canonicalBoneMap: rig.binding.canonicalToSourceBone,
            worldCorrection: rig.binding.worldCorrection,
          },
          beforeExport: () => rig.resetToRest(),
          afterExport: () => rig.applyPose(livePose, liveHipsY),
          stripTextures: options.stripTextures === true,
          // Only meaningful when stripping; passing it always keeps the two call shapes identical.
          materialSource: session.model?.materialSource,
        });

        const inspection = await inspectGlb(buffer);
        if (inspection.clips.length !== clips.length) {
          throw new AppError(
            'GLB_EXPORT_INVALID',
            `The exported GLB contains ${inspection.clips.length} animation(s) but ` +
              `${clips.length} were selected.`,
            { exported: inspection.clips.map((c) => c.name) },
          );
        }
        if (inspection.hasPositionOrScaleTracks) {
          throw new AppError(
            'GLB_EXPORT_INVALID',
            'The exported GLB contains position or scale tracks, which breaks the rotation-only ' +
              'invariant.',
            { clips: inspection.clips },
          );
        }

        return { buffer, inspection };
      });
    },

    async exportGlbDownload(
      filename?: string,
      options: ExportGlbOptions = {},
    ): Promise<GlbInspection> {
      return guard(ctx, 'The GLB could not be exported.', async () => {
        const project = requireProject(ctx);
        const stripTextures = options.stripTextures === true;
        const name = withGlbExtension(filename ?? project.name);

        /*
         * The textures come out of the source GLB BEFORE the export runs, so a model whose images
         * cannot be recovered never produces a half-shipped character: a `.glb` with no textures
         * in it and no files to put back is worse than no export at all.
         *
         * `session.sourceGlb` rather than a read from IndexedDB — it is the exact buffer the
         * on-screen model was built from, set on both the import and the reopen path, so the
         * manifest can never describe a different file than the one the user is looking at.
         */
        let extracted: ExtractedTextures | null = null;
        if (stripTextures) {
          const source = session.sourceGlb;
          if (!source) {
            throw new AppError(
              'EXPORT_NO_TEXTURE_SOURCE',
              'The original GLB for this project is not loaded, so its textures cannot be ' +
                'extracted. Reopen the project and try again, or export with textures.',
            );
          }
          /*
           * An EMPTY map means "this loader gave us no associations", not "this model uses no
           * materials" — passing it as the filter would quietly extract nothing and report a
           * model with no textures. Absent is the honest way to say "no opinion".
           */
          const used = session.model?.materialSource;
          extracted = extractTextures(source, {
            modelFileName: name,
            baseName: safeFileName(stemOf(name)),
            usedMaterials: used && used.size > 0 ? new Set(used.values()) : undefined,
          });
          if (!extracted) {
            throw new AppError(
              'EXPORT_NO_TEXTURE_SOURCE',
              'The original file for this project could not be read as a GLB, so its textures ' +
                'cannot be extracted.',
            );
          }
        }

        const { buffer, inspection } = await controller.exportGlbBuffer(options);
        downloadBlob(new Blob([buffer], { type: 'model/gltf-binary' }), name);

        if (extracted && extracted.files.length > 0) {
          // The manifest first: it is the file that explains the others, so if the browser's
          // multiple-download prompt interrupts the batch, the user has the map before the pieces.
          downloadBlob(
            new Blob([textureManifestToJson(extracted.manifest)], { type: 'application/json' }),
            `${stemOf(name)}.textures.json`,
          );
          for (const file of extracted.files) {
            // `.buffer` rather than the view: `bytes` is already an exact `slice`, so its buffer
            // is exactly this image, and `BlobPart` will not take a `Uint8Array<ArrayBufferLike>`.
            downloadBlob(
              new Blob([file.bytes.buffer as ArrayBuffer], { type: file.mimeType }),
              file.file,
            );
          }
        }
        /*
         * The summary is written on the DOWNLOAD path only. `exportGlbBuffer` is the test surface
         * and the "did it round trip" probe; a probe must not overwrite the record of what the
         * user last actually shipped.
         */
        store.actions.setLastExport({
          filename: name,
          boneNames: inspection.boneNames,
          clips: inspection.clips.map((c) => ({ name: c.name, duration: c.duration })),
          hasPositionOrScaleTracks: inspection.hasPositionOrScaleTracks,
          hipsPositionTrackCount: inspection.hipsPositionTrackCount,
          // Always written, never merged: `setLastExport` replaces the whole summary, and a
          // stripped export followed by a normal one must not leave the panel claiming texture
          // files that the last export never produced.
          textures: extracted
            ? {
                fileCount: extracted.files.length,
                totalBytes: extracted.totalBytes,
                manifestFile: `${stemOf(name)}.textures.json`,
                materialCount: extracted.manifest.materials.length,
                warnings: extracted.manifest.warnings,
              }
            : null,
          at: Date.now(),
        });
        /*
         * The unsaved-changes note is folded INTO the summary rather than raised as a second
         * toast: `ui.toast` is a single slot, so a follow-up notification would simply erase the
         * line that says what was exported. One sentence, one toast, and the user learns the GLB
         * carries work that is not in the library yet — the thing the audit found nothing said.
         */
        const unsaved = unsavedClipName;
        store.actions.toast(
          'info',
          (inspection.clips.length === 0
            ? `Exported rig-only GLB, ${inspection.boneNames.length} bones.`
            : `Exported ${inspection.clips.length} clip(s), ${inspection.boneNames.length} bones.`) +
            textureNote(extracted) +
            (unsaved ? ` Includes unsaved changes to "${unsaved}".` : ''),
        );
        return inspection;
      });
    },
  };

  return controller;
}
